import React, { useEffect, useState } from "react";
import { useAuth } from "@/AuthContext";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Printer, IdCard, Building2 } from "lucide-react";
import QRCode from "qrcode";
import { generateCardIdPDF } from "@/lib/cardId";

export default function SalarieCardID() {
  const { user } = useAuth();
  const [qrDataUrl, setQrDataUrl] = useState("");

  useEffect(() => {
    if (!user?.code) return;
    const profileUrl = `${window.location.origin}/login/salarie?code=${encodeURIComponent(user.code)}`;
    QRCode.toDataURL(profileUrl, { margin: 1, width: 256, color: { dark: "#0A0A0A", light: "#FFFFFF" } })
      .then(setQrDataUrl).catch(() => {});
  }, [user?.code]);

  const handleDownloadPDF = async () => {
    try {
      const doc = await generateCardIdPDF({
        salarie: user,
        entreprise: user.entreprise,
        qrDataUrl,
      });
      doc.save(`carte-id-${user.code}.pdf`);
      toast.success("Carte téléchargée");
    } catch (e) { toast.error("Erreur de génération"); }
  };

  const handlePrint = () => window.print();

  if (!user) return null;

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Identité"
        title="Ma carte ID"
        sub="Votre carte professionnelle PAG. Téléchargez-la en PDF ou imprimez-la directement."
        right={
          <div className="flex gap-2">
            <Button onClick={handlePrint} data-testid="card-print-btn" variant="outline" className="h-11"><Printer className="h-4 w-4 mr-2" /> Imprimer</Button>
            <Button onClick={handleDownloadPDF} data-testid="card-download-btn" className="h-11 bg-[#002FA7] text-white"><Download className="h-4 w-4 mr-2" /> Télécharger PDF</Button>
          </div>
        }
      />

      <div className="p-6 lg:p-10">
        <div className="max-w-3xl">
          {/* Carte web (recto) — format carte de crédit */}
          <div data-testid="card-id-preview" className="card-id relative w-full max-w-[480px] aspect-[1.586/1] bg-[#0A0A0A] text-white shadow-2xl print:shadow-none flex">
            {/* Bandeau Klein Blue */}
            <div className="w-3 bg-[#002FA7]" />
            <div className="flex-1 p-5 lg:p-6 flex flex-col">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#FFD700] font-bold">// PAG Business Manager</div>
                  <h2 className="font-display text-xl lg:text-2xl font-black tracking-tighter mt-1">CARTE D'IDENTITÉ</h2>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-semibold">Professionnelle</div>
                </div>
                {qrDataUrl && (
                  <div className="bg-white p-1.5">
                    <img src={qrDataUrl} alt="QR Code" className="h-16 w-16 lg:h-20 lg:w-20" data-testid="card-qr" />
                  </div>
                )}
              </div>

              <div className="mt-auto space-y-1">
                <div className="font-display font-black text-lg lg:text-xl tracking-tight" data-testid="card-name">
                  {(user.prenom || "").toUpperCase()} {(user.nom || "").toUpperCase()}
                </div>
                <div className="text-[10px] text-white/70">
                  Poste : <span className="text-white font-semibold">{user.poste || "—"}</span>
                </div>
                <div className="text-[10px] text-white/70">
                  Contrat : <span className="text-white font-semibold">{user.contrat || "—"}</span>
                </div>
                <div className="font-mono text-base text-[#FFD700] font-bold mt-2 tracking-wide" data-testid="card-code">{user.code}</div>
                <div className="text-[8px] uppercase tracking-[0.25em] text-white/50 font-semibold">Code d'accès PAG</div>
              </div>

              <div className="mt-3 pt-3 border-t border-white/20 flex justify-between items-end">
                <div>
                  <div className="text-[10px] font-semibold flex items-center gap-1.5"><Building2 className="h-3 w-3" /> {user.entreprise?.name || "—"}</div>
                  <div className="text-[9px] text-white/50 font-mono">{user.entreprise?.code || ""}</div>
                </div>
                <div className="bg-[#FFD700] text-[#0A0A0A] px-2 py-0.5 text-[9px] uppercase tracking-wider font-bold">
                  Édition 2026
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white border border-[#DEE2E6] p-5 lg:p-6 print:hidden">
            <div className="flex items-start gap-3 mb-4">
              <IdCard className="h-5 w-5 text-[#002FA7] mt-0.5" />
              <div>
                <h3 className="font-display font-bold text-base">Comment l'utiliser ?</h3>
                <p className="text-sm text-[#495057] mt-1">Cette carte contient votre <b>code d'accès PAG</b> et un <b>QR code</b>. Scannez-le pour accéder rapidement à la page de connexion. Imprimez-la ou conservez le PDF.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Info label="Code" value={user.code} />
              <Info label="Entreprise" value={user.entreprise?.name || "—"} />
              <Info label="Poste" value={user.poste || "—"} />
              <Info label="Contrat" value={user.contrat || "—"} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          aside, header, .card-id ~ * { display: none !important; }
          .card-id { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="border border-[#DEE2E6] p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold">{label}</div>
      <div className="font-mono font-bold text-sm mt-1">{value}</div>
    </div>
  );
}
